// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import CheckIcon from '@mui/icons-material/Check'
import OpacityIcon from '@mui/icons-material/Opacity'
import { Button, ListItemIcon, ListItemText, Menu, MenuItem, Tooltip } from '@mui/material'
import { useState } from 'react'
import { useTranslate } from 'react-admin'
import { type GlucoseUnit, useGlucoseUnitPreference } from '../hooks/useGlucoseUnitPreference'

const UNIT_LABEL_KEY: Record<GlucoseUnit, string> = {
  'mmol/L': 'pos.profile.glucose_unit_mmol',
  'mg/dL': 'pos.profile.glucose_unit_mgdl',
}

const UNITS: GlucoseUnit[] = ['mmol/L', 'mg/dL']

// Per-unit accent colors for the app-bar chip. Deliberately chosen OUTSIDE the
// TIR clinical palette (red/amber/green/blue/indigo) so a vet never misreads the
// unit as a glucose-status signal: teal = mmol/L (international), violet = mg/dL
// (US). Both are far apart on the wheel and high-contrast on the white app bar.
const UNIT_COLOR: Record<GlucoseUnit, string> = {
  'mmol/L': '#0D9488',
  'mg/dL': '#7C3AED',
}

/**
 * App-bar glucose-unit switcher, sitting beside the language menu. Moves the
 * display-unit choice out of the buried My Profile dialog so a vet can flip
 * mmol/L ↔ mg/dL in one click from anywhere. Persistence (store + backend
 * PATCH + identity-cache invalidation + revert-on-failure) is delegated to
 * useGlucoseUnitPreference, the single write path shared with the profile and
 * chart-header toggles, so the unit can never diverge across surfaces.
 */
export const GlucoseUnitMenuButton = () => {
  const translate = useTranslate()
  const { glucoseUnit, changeGlucoseUnit, saving } = useGlucoseUnitPreference()
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null)

  const handleSelect = (unit: GlucoseUnit) => {
    setAnchorEl(null)
    if (unit !== glucoseUnit) changeGlucoseUnit(unit)
  }

  const accent = UNIT_COLOR[glucoseUnit]

  return (
    <>
      <Tooltip title={translate('pos.profile.glucose_unit')}>
        <Button
          color="inherit"
          size="small"
          onClick={(e) => setAnchorEl(e.currentTarget)}
          startIcon={<OpacityIcon fontSize="small" />}
          disabled={saving}
          aria-label={translate('pos.profile.glucose_unit')}
          data-testid="glucose-unit-button"
          sx={{
            textTransform: 'none',
            fontWeight: 700,
            color: accent,
            border: `1px solid ${accent}`,
            borderRadius: 1.5,
            px: 1.25,
            '& .MuiButton-startIcon': { color: accent },
            '&:hover': { borderColor: accent, backgroundColor: `${accent}14` },
            '&.Mui-disabled': { color: accent, borderColor: `${accent}66`, opacity: 0.6 },
          }}
        >
          {glucoseUnit}
        </Button>
      </Tooltip>
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        {UNITS.map((unit) => (
          <MenuItem
            key={unit}
            selected={unit === glucoseUnit}
            onClick={() => handleSelect(unit)}
            data-testid={`glucose-unit-option-${unit === 'mmol/L' ? 'mmol' : 'mgdl'}`}
          >
            <ListItemIcon>
              {unit === glucoseUnit ? <CheckIcon fontSize="small" /> : null}
            </ListItemIcon>
            <ListItemText>{translate(UNIT_LABEL_KEY[unit])}</ListItemText>
          </MenuItem>
        ))}
      </Menu>
    </>
  )
}
